// src/layouts/MainLayout.tsx
import React, { useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { DefaultFeatureFlags, isFeatureEnabled } from "@/utils/featureFlags";
import { cn } from "@/utils/cn";
import { ProfileMenu } from "@/components/molecules/ProfileMenu/ProfileMenu";
import { useAuth } from "@/modules/auth/features/application/hooks/useAuthQueries";
import { useMobile } from "@/hooks/useMobile";
import NotificationComponent from "@/components/organisms/Notification/Notification";
import { useNotification } from "@/modules/notifications/application/hook/useNotification";
import { useTheme } from "@/core/theme/ThemeProvider";
import { useLogout } from "@/modules/auth/features/login/application/hooks/useLoginQueries";
import { BellIcon, MoonIcon, SunIcon } from "@heroicons/react/24/outline";

import { Sidebar } from "@/components/organisms/Sidebar/Sidebar";
import { SidebarTrigger } from "@/components/atoms/Sidebar";
import { TitleProvider } from "@/core/context/TitleContext";
import { DialogProvider } from "@/core/context/DialogContext";
import { SidebarProvider, useSidebar } from "@/core/context/SideBarContext";

export const MainLayout: React.FC = () => {
  return (
    <TitleProvider>
      <SidebarProvider>
        <DialogProvider>
          <LayoutContent />
        </DialogProvider>
      </SidebarProvider>
    </TitleProvider>
  );
};

const LayoutContent: React.FC = () => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const useNeumorphism = isFeatureEnabled(DefaultFeatureFlags.USE_NEUMORPHISM);
  const { mutate: logout } = useLogout();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { notifications, addNotification, removeNotification } = useNotification();
  const { open } = useSidebar();

  const handleLogout = useCallback(() => {
    logout();
    navigate("/auth/login", { replace: true });
  }, [logout, navigate]);

  const handleClickNotification = () => {
    addNotification("¡Tienes una orden pendiente!", "info");
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden w-full">
      {/* Sidebar + context */}
      <Sidebar />

      {/* Contenido principal */}
      <div
        className={cn(
          "flex flex-col flex-1 max-w-full overflow-x-hidden transition-all duration-300",
          !open || isMobile ? "ml-0" : "ml-64"
        )}
      >
        {/* Header */}
        <header className="h-16 flex items-center px-6 sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-md">
          <SidebarTrigger />
          <div className="flex items-end space-x-4 ml-auto">
            {isFeatureEnabled(DefaultFeatureFlags.NOTIFICATIONS) && (
              <button
                onClick={handleClickNotification}
                className={cn(
                  "rounded-full p-2",
                  useNeumorphism
                    ? "button-neumorph"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                )}
              >
                <BellIcon className="h-6 w-6" />
              </button>
            )}
            {isFeatureEnabled(DefaultFeatureFlags.ENABLE_DARK_MODE) && (
              <button
                onClick={toggleDarkMode}
                className={cn(
                  "rounded-full p-2",
                  useNeumorphism
                    ? "button-neumorph"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                )}
              >
                {isDarkMode ? (
                  <SunIcon className="h-6 w-6" />
                ) : (
                  <MoonIcon className="h-6 w-6" />
                )}
              </button>
            )}
            <ProfileMenu
              name={user?.name || ""}
              useNeumorphism={useNeumorphism}
              onLogout={handleLogout}
            />
          </div>
        </header>

        {/* Main outlet */}
        <main className="flex-1 overflow-auto py-3 min-h-0">
          <Outlet />
        </main>

        {/* Notificaciones */}
        <NotificationComponent
          notifications={notifications}
          removeNotification={removeNotification}
          useNeumorphism={useNeumorphism}
        />
      </div>
    </div>
  );
};
